import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { FlightItinerary } from '../interfaces';
import { Segment } from '../../../api/api-checkout/models/rq-checkout-search';
import { GlobalComponent } from '../../../shared/global';
import { DateUtils } from '../../../shared/utils/date-utils';
import { environment } from '../../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  RDiscount,
  RDiscountCupon,
  Result,
} from '../../../api/api-checkout/models/rq-checkout-discount';
import { UpsellService } from '../../../services/upsell/upsell.service';
import { take, timeout } from 'rxjs/operators';
import { RValidateBooking } from '../../../api/api-checkout/models/rq-checkout-validate-booking';
import { isPlatformBrowser } from '@angular/common';
import { UserStorage } from '../../../services/user-account/accounts.service';
import { RPurchase } from '../../../api/api-checkout/models/rq-checkout-save-booking';
import { WebLink } from '../../../api/api-nmviajes/models/rq-token-ce-request';
import { BookingInsuranceService } from './booking-insurance.service';
import { FlightDataService } from './flight-data.service';
import { BookingOrigin, BookingFlowStatus } from '../constants';
import { BrevoService } from '../../../services/brevo';
import { AbandonedCartEventsEnum } from '../../checkout-page/constants';

export const OID_BO = 'SRZHM38BP';

@Injectable({
  providedIn: 'root',
})
export class BookingService {
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private equifaxContactData = new Subject<any>();
  equifaxContactData$ = this.equifaxContactData.asObservable();

  private bin = new BehaviorSubject<string>('');
  bin$ = this.bin.asObservable();

  private isBookingSubmitted = new Subject<boolean>();
  isBookingSubmitted$ = this.isBookingSubmitted.asObservable();

  private discount = new Subject<RDiscount | null>();
  discount$ = this.discount.asObservable();

  private safetyPayCode = new BehaviorSubject<string>('');
  safetyPayCode$ = this.safetyPayCode.asObservable();

  private bookingStatus = new BehaviorSubject<BookingFlowStatus>(BookingFlowStatus.Init);
  bookingStatus$ = this.bookingStatus.asObservable();

  flightClassName: string;
  transactionId: string;
  discountAmount?: number;

  origin: BookingOrigin = BookingOrigin.SEARCH;
  booking: any;
  orderId: number;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    private readonly httpClient: HttpClient,
    private readonly upsellService: UpsellService,
    private readonly insuranceService: BookingInsuranceService,
    private readonly flightDataService: FlightDataService,
    private readonly brevoService: BrevoService
  ) {}

  setEquifaxContactData(data: any) {
    this.equifaxContactData.next(data);
  }

  getDiscounts(promotionalCode = '') {
    const groupId = this.flightDataService.getFlight()!.id;
    const upSellName = this.upsellService.getSelectedUpsell()?.name ?? '';
    const bin = this.bin.getValue();
    const insurance = this.insuranceService.getValue();

    const url = `${environment.urlApiMotorVuelos}/mv/payment/get-discounts?Bin=${bin}&GroupId=${groupId}&IncludeSecure=${!!insurance}&PromotionalCode=${promotionalCode}&BrandedFareName=${upSellName}`;
    const headers = new HttpHeaders().set(
      'Authorization',
      `Bearer ${GlobalComponent.tokenMotorVuelo}`
    );
    return this.httpClient.get<RDiscount>(url, { headers });
  }

  getPromocionalCode(code: string) {
    const credentials = this.getUser();

    const url = `${environment.urlApiMotorVuelos}/mv/payment/get-promotional-code-by-code?Code=${code}&GroupId=${this.flightDataService.getFlight()!.id}&Email=${
      credentials ? credentials.email : ''
    }&BrandedFareName=${this.upsellService.getSelectedUpsell()?.name ?? ''}`;
    const headers = new HttpHeaders()
      .set('not-loading', 'true')
      .set('Authorization', `Bearer ${GlobalComponent.tokenMotorVuelo}`);
    return this.httpClient.get<RDiscountCupon>(url, { headers }).pipe(timeout(5000));
  }

  isBoliviaOID() {
    const flight = this.flightDataService.getFlight();

    if (flight) {
      const { gds, departure, returns } = flight;
      return (
        gds.officeId === OID_BO ||
        departure.some((item: any) => item.gds?.officeId === OID_BO) ||
        (returns ? (returns as any).gds?.officeId === OID_BO : false)
      );
    }

    return false;
  }

  setBin(bin: string) {
    this.bin.next(bin);
  }

  emitBookingSubmit() {
    this.isBookingSubmitted.next(true);
  }

  validateBooking(itinerary: FlightItinerary, booking: any) {
    const url = `${environment.urlApiMotorVuelos}/mv/validate-booking`;

    let orientation: number;
    let searchRoute = '';
    let multiCityDates = '';
    let segments: any[] = [];
    let passengers: any[];

    if (itinerary.flight.returns) orientation = 0;
    else if (itinerary.flight.departure.length === 1) orientation = 1;
    else orientation = 2;

    itinerary.flight.departure.forEach((item, i) => {
      searchRoute += `${
        searchRoute === '' ? '' : ';'
      }${item.originCity.code},${item.destinationCity.code}`;
      segments.push({
        ciudadOrigen: item.originCity.code,
        ciudadDestino: item.destinationCity.code,
      });
      if (i > 0)
        multiCityDates += `${
          multiCityDates === '' ? '' : ';'
        }${item.departureDate.replace(/-/g, '/')}`;
    });

    passengers = booking.passengers.map((item: any) => {
      let documentType = 'DNI';
      if (item.documentType === '1') documentType = 'CEX';
      else if (item.documentType === '2') documentType = 'PSP';

      return {
        nombre: item.name,
        apellido: item.lastname,
        numeroDocument: item.documentNumber,
        tipoDocumento: documentType,
      };
    });

    const body = {
      idWeb: 7,
      idLang: 1,
      orientacion: orientation,
      rutaBusqueda: searchRoute,
      fechaSalida: itinerary.flight.departure[0].departureDate.replace(/-/g, '/'),
      fechaRetorno: itinerary.flight.returns
        ? itinerary.flight.returns.departureDate.replace(/-/g, '/')
        : itinerary.flight.departure[0].departureDate.replace(/-/g, '/'),
      fechasMulticity: multiCityDates,
      esRutaNacional: itinerary.isDomestic,
      idLogSearch: 1,
      pasajeros: passengers,
      segmentos: segments,
      lineaValidadora:
        itinerary.flight.departure[0].segments[0].flightSegments[0].operatingAirline.code,
    };

    const headers = new HttpHeaders().set(
      'Authorization',
      `Bearer ${GlobalComponent.tokenMotorVuelo}`
    );
    return this.httpClient.post<RValidateBooking>(url, body, { headers });
  }

  onBookingSuccess(data: RPurchase, booking: unknown) {
    this.booking = booking;
    this.setBookingStatus(BookingFlowStatus.Success);
    this.orderId = data.idCotizacion;

    if (this.brevoService.contact)
      this.brevoService
        .trackEvent(AbandonedCartEventsEnum.ORDER_COMPLETED)
        .pipe(take(1))
        .subscribe();

    const id = data.resultPasarela?.Transaction_id ?? data.ciP_SafetyPAY;
    if (id) this.setSafetyPayId(id.toString());
  }

  setBookingStatus(status: BookingFlowStatus) {
    this.bookingStatus.next(status);
  }

  getBookingStatusValue(): BookingFlowStatus {
    return this.bookingStatus.value;
  }

  setSafetyPayId(id: string) {
    this.safetyPayCode.next(id);
  }

  getSafetyPayIdValue(): string {
    return this.safetyPayCode.value;
  }

  sendEmail(purchase: RPurchase, booking: any, discount?: Result) {
    const url = `${environment.urlApiMotorVuelos}/mv/send-booking`;
    const body = this.buildEmailBody(purchase, booking, discount);
    const headers = new HttpHeaders()
      .set('not-loading', 'true')
      .set('Authorization', `Bearer ${GlobalComponent.tokenMotorVuelo}`);
    return this.httpClient.post<any>(url, body, { headers });
  }

  private getUser() {
    let user = null;
    if (this.isBrowser) user = localStorage.getItem('usuario');
    return user ? (JSON.parse(user) as UserStorage) : null;
  }

  private buildEmailBody(purchase: RPurchase, booking: any, discount?: Result): any {
    const data = this.flightDataService.getValue()!;
    const flight = data.flight;
    const itinerary: any[] = [];
    const segments: Segment[] = [];
    const departureSegment = flight.returns
      ? data.segments.slice(0, data.segments.length - 1)
      : data.segments;

    flight.departure.forEach((item: any) => {
      item.segments.forEach((segment: any) => {
        if (departureSegment.includes(segment.segmentId)) {
          segment.isDeparture = true;
          segments.push(segment);
        }
      });
    });

    if (flight.returns) {
      const returnSegment = data.segments[data.segments.length - 1];
      flight.returns.segments.forEach((item: any) => {
        item.isDeparture = false;
        if (returnSegment === item.segmentId) segments.push(item);
      });
    }

    segments.forEach((item) => {
      const flights = item.flightSegments.map((segment) => segment.flightNumber.toString(10));
      itinerary.push({
        ciudadOrigen: item.flightSegments[0].departureAirport.name,
        ciudadDestino: item.flightSegments[item.flightSegments.length - 1].arrivalAirport.name,
        duracion: `${item.flightDuration.split('.')[0]}h ${item.flightDuration.split('.')[1]}m`,
        fechaSalida: DateUtils.formatDateStringToShortDateFormat(
          item.flightSegments[0].departureDateTime
        ),
        fechaLlegada: DateUtils.formatDateStringToShortDateFormat(
          item.flightSegments[item.flightSegments.length - 1].arrivalDateTime
        ),
        horaSalida: DateUtils.formatDateStringToHoursFormat(
          item.flightSegments[0].departureDateTime
        ),
        horaLlegada: DateUtils.formatDateStringToHoursFormat(
          item.flightSegments[item.flightSegments.length - 1].arrivalDateTime
        ),
        vuelos: flights,
        operadorPor: item.flightSegments[0].marketingAirline.name,
        escalas: item.flightSegments.length,
        equipajeMano:
          this.upsellService.getSelectedUpsell()?.[
            item.isDeparture ? 'includeHandBagDep' : 'includeHandBagRet'
          ] ?? item.equipaje.cabina?.piezas === 1,
        equipajeBodega:
          this.upsellService.getSelectedUpsell()?.[
            item.isDeparture ? 'includesHoldBagDep' : 'includesHoldBagRet'
          ] ?? item.equipaje.piezas === 1,
        adicionales: [],
      });
    });

    const passengers = booking.passengers.map((item: any) => {
      const birthday = `${('0' + item.day).slice(0, 2)}/${('0' + item.month).slice(0, 2)}/${item.year}`;
      return {
        tipoPasajero: item.type,
        nombresApellidos: item.name + ' ' + item.lastname,
        fechaNacimiento: birthday,
        numeroSolicitudCompra: purchase.idCotizacion.toString(),
      };
    });

    const authLinks = this.getWebLinks();

    let termsConditions = '';
    let travelDocuments = '';
    let bookingConditions = '';
    let privacyPolicies = '';

    if (authLinks && authLinks.length) {
      termsConditions = authLinks.find((link) => link.tipoEnlace === 'TC')?.link ?? '';
      travelDocuments = authLinks.find((link) => link.tipoEnlace === 'DV')?.link ?? '';
      bookingConditions = authLinks.find((link) => link.tipoEnlace === 'CR')?.link ?? '';
      privacyPolicies = authLinks.find((link) => link.tipoEnlace === 'PP')?.link ?? '';
    }

    return {
      parameter: {
        subject: `Solicitud de Compra Nro. ${purchase.idCotizacion} para ${booking.contact.name} ${booking.contact.lastname}`,
        to: [booking.contact.email],
        data: {
          numeroSolicitudCompra: purchase.idCotizacion.toString(10),
          messageConfirm: `Tu solicitud de compra ${purchase.idCotizacion} ha sido recibida y está siendo procesada.`,
          allowVoid: false,
          agil: false,
          itinerario: itinerary,
          precioFinal: {
            precioDolares: `$ ${(
              Number(data.flight.detailPricing?.totalPay) +
              Number(booking.secure?.totalPrice ?? 0) -
              (discount ? discount.amountDiscount : 0)
            ).toFixed(2)}`,
            precioSoles: `$ ${(
              Number(data.flight.detailPricing?.totalPay) +
              Number(booking.secure?.totalPrice ?? 0) -
              (discount ? discount.amountDiscount : 0)
            ).toFixed(2)}`,
          },
          pasajeros: passengers,
          contactos: [
            {
              nombresApellidos: booking.contact.name + ' ' + booking.contact.lastname,
              correoElectronico: booking.contact.email,
              telefonos: `(${booking.contact.phoneCode}) ${booking.contact.phoneNumber}`,
            },
          ],
        },
        webLink: { bookingConditions, privacyPolicies, termsConditions, travelDocuments },
      },
    };
  }

  private getWebLinks(): WebLink[] {
    let storedAuthLinks: string | null;
    if (this.isBrowser) {
      storedAuthLinks = sessionStorage.getItem('AUTH_LINKS');
      if (storedAuthLinks) return JSON.parse(storedAuthLinks) as WebLink[];
    }
    return [];
  }

  setDiscount(value: RDiscount | null) {
    this.discount.next(value);
  }
}
